import React, { useState, useEffect, useRef } from 'react';
import { Button, message, Select } from 'antd';
import { ReloadOutlined, ClearOutlined, PlayCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import logsWebSocketService from '../../services/logsWebSocket';
import { CreateExecutionDto, ExecutionResponseDto } from '../../types';

const coreService = apiService.getService(ServiceType.CORE);

const LogContainer = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ $compact }) =>
    $compact
      ? `
        padding: 6px;
        height: 100%;
        background: ${({ theme }: any) => theme.colors.background.secondary};
        border: none;
        border-radius: 4px;
        overflow: hidden;
      `
      : `
        height: 100%;
        min-height: 400px;
        background: ${({ theme }: any) => theme.colors.background.secondary};
        border: 1px solid ${({ theme }: any) => theme.colors.border.primary};
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      `
  }
`;

const LogHeader = styled.div<{ $compact?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ $compact }) => $compact ? '4px' : '8px'};
  flex-shrink: 0;
  ${({ $compact }) =>
    $compact
      ? `
        margin-bottom: 4px;
        padding: 2px 4px;
      `
      : `
        padding: 16px 20px;
        background: ${({ theme }: any) => theme.colors.background.tertiary};
        border-bottom: 1px solid ${({ theme }: any) => theme.colors.border.primary};
      `
  }
`;

const LogTitle = styled.div<{ $compact?: boolean }>`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.3px;
`;

const LogActions = styled.div<{ $compact?: boolean }>`
  display: flex;
  gap: ${({ theme, $compact }) => $compact ? '8px' : theme.spacing.sm};
  align-items: center;
`;

const ExecutionControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing.md};
  padding-right: ${({ theme }) => theme.spacing.md};
  border-right: 1px solid ${({ theme }) => theme.colors.border.primary};
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    margin-right: 0;
    padding-right: 0;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
    padding-bottom: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    width: 100%;
  }
`;

const LogContent = styled.div<{ $compact?: boolean }>`
  ${({ $compact }) =>
    $compact
      ? `
        flex: 1;
        padding: 6px 8px;
        font-size: 12px;
        line-height: 1.6;
        background: ${({ theme }: any) => theme.colors.background.secondary};
        border: none;
        border-radius: 4px;
      `
      : `
        flex: 1;
        padding: 16px 20px;
        font-size: 12px;
        line-height: 1.8;
        background: ${({ theme }: any) => theme.colors.background.secondary};
      `
  }
  overflow-y: auto;
  overflow-x: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    'Noto Color Emoji';
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-word;

  /* Custom scrollbar - more visible */
  &::-webkit-scrollbar {
    width: ${({ $compact }) => $compact ? '6px' : '10px'};
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme, $compact }: any) => 
      $compact 
        ? theme.colors.background.secondary 
        : theme.colors.background.tertiary
    };
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.primary};
    border-radius: 5px;
    border: 2px solid ${({ theme, $compact }: any) => 
      $compact 
        ? theme.colors.background.secondary 
        : theme.colors.background.tertiary
    };

    &:hover {
      background: ${({ theme }) => theme.colors.text.secondary};
    }
  }
`;

const LogLine = styled.div<{ $level?: 'info' | 'error' | 'warning' | 'success'; $compact?: boolean }>`
  margin-bottom: ${({ $compact }) => $compact ? '1px' : '4px'};
  padding: ${({ $compact }) => $compact ? '1px 3px' : '2px 4px'};
  border-radius: 3px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background.tertiary};
  }
  
  color: ${({ theme, $level = 'info' }) => {
    switch ($level) {
      case 'error':
        return '#FF6B6B';
      case 'warning':
        return '#FFA726';
      case 'success':
        return '#66BB6A';
      default:
        return theme.colors.text.primary;
    }
  }};
  
  font-weight: ${({ $level }) => {
    switch ($level) {
      case 'error':
        return '500';
      case 'warning':
        return '500';
      default:
        return '400';
    }
  }};
`;

const EmptyState = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  font-style: italic;
  padding: 20px;
`;

interface LogViewerProps {
  selectedPortfolio?: {
    id: string;
    portfolioId: string;
    portfolioName: string;
  } | null;
  strategyId?: string | null;
  /**
   * Optional strategy-type scope (e.g. "GRID", "PAIR_TRADING"). Set by each
   * strategy page so the execution dropdown only shows runs of that type —
   * without it a user running both grid and pair on the same portfolio will
   * see cross-strategy executions on either page.
   */
  strategyType?: string | null;
  executionId?: string | null;
  compact?: boolean;
  showExecutionControls?: boolean;
  refreshKey?: number;
}

const LogViewer: React.FC<LogViewerProps> = ({
  selectedPortfolio,
  strategyId,
  strategyType,
  executionId: executionIdProp,
  compact = false,
  showExecutionControls = true,
  refreshKey,
}) => {
  const [logs, setLogs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [creatingExecution, setCreatingExecution] = useState(false);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [executions, setExecutions] = useState<ExecutionResponseDto[]>([]);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [executionStatus, setExecutionStatus] = useState<'PASSED' | 'FAILED' | 'RUNNING' | 'PENDING'>('PASSED');
  const logContentRef = useRef<HTMLDivElement>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Use executionId from prop if provided, otherwise use internal state
  const effectiveExecutionId = executionIdProp || selectedExecutionId;

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logContentRef.current) {
      logContentRef.current.scrollTop = logContentRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Fetch logs from server
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        message.error('Please login first');
        return;
      }

      // If we have a selected execution, fetch logs from execution endpoint
      if (effectiveExecutionId) {
        try {
          const response = await coreService.get(`/bot/executions/${effectiveExecutionId}/log`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            }
          });

          if (response.data?.logContent) {
            // Parse logContent string into array of lines
            const logLines = response.data.logContent.split('\n').filter((line: string) => line.trim() !== '');
            setLogs(logLines);
          } else {
            setLogs([]);
          }
        } catch (error: any) {
          console.error('Error fetching execution logs:', error);
          if (error.response?.status === 404) {
            setLogs([]);
          } else {
            message.error('Failed to fetch execution logs');
          }
        }
      } else {
        // Fallback to old endpoint if no execution selected
        const response = await coreService.get('/bot', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          params: {
            portfolioId: selectedPortfolio?.portfolioId || '',
            limit: compact ? 500 : 1000
          }
        });

        if (response.data && Array.isArray(response.data)) {
          setLogs(response.data);
        } else if (response.data?.logs && Array.isArray(response.data.logs)) {
          setLogs(response.data.logs);
        } else if (typeof response.data === 'string') {
          setLogs(response.data.split('\n'));
        }
      }
    } catch (error: any) {
      console.error('Error fetching logs:', error);
      if (error.response?.status === 404) {
        setLogs([
          `[${new Date().toISOString()}] INFO: Log endpoint not configured`,
          `[${new Date().toISOString()}] INFO: Please configure /bot endpoint on server`,
          `[${new Date().toISOString()}] INFO: Portfolio: ${selectedPortfolio?.portfolioName || 'None selected'}`,
        ]);
      } else {
        message.error('Failed to fetch logs');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch executions list (only if showExecutionControls is true)
  useEffect(() => {
    if (!showExecutionControls) return;

    const fetchExecutions = async () => {
      const token = localStorage.getItem('access_token');
      if (!token || !selectedPortfolio?.portfolioId) {
        return;
      }

      try {
        setLoadingExecutions(true);
        const response = await coreService.get<ExecutionResponseDto[]>('bot/executions', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          params: {
            portfolioId: selectedPortfolio.portfolioId,
            strategyId: strategyId || undefined,
            strategyType: strategyType || undefined,
          }
        });

        if (response.data && Array.isArray(response.data)) {
          setExecutions(response.data);
          // Auto-select the first execution if available and none selected
          if (response.data.length > 0 && !effectiveExecutionId) {
            setSelectedExecutionId(response.data[0].id);
          }
        }
      } catch (error: any) {
        console.error('Error fetching executions:', error);
        if (error.response?.status !== 404) {
        }
      } finally {
        setLoadingExecutions(false);
      }
    };

    fetchExecutions();
  }, [selectedPortfolio?.portfolioId, strategyId, strategyType, showExecutionControls]);

  // Auto-fetch latest execution logs in compact mode (no execution controls)
  useEffect(() => {
    if (!showExecutionControls && selectedPortfolio?.portfolioId) {
      const fetchLatestExecution = async () => {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        try {
          const res = await coreService.get<ExecutionResponseDto[]>('bot/executions', {
            headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
            params: {
              portfolioId: selectedPortfolio.portfolioId,
              strategyId: strategyId || undefined,
              strategyType: strategyType || undefined,
            },
          });
          const list = Array.isArray(res.data) ? res.data : [];
          if (list.length > 0) {
            setSelectedExecutionId(list[0].id);
          }
        } catch (err) {
          console.error('Error fetching executions for compact log:', err);
        }
      };
      fetchLatestExecution();
    }
  }, [selectedPortfolio?.portfolioId, strategyId, strategyType, showExecutionControls, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to execution logs when effectiveExecutionId changes
  useEffect(() => {
    const subscribeToLogs = async () => {
      if (!effectiveExecutionId) {
        return;
      }

      const token = localStorage.getItem('access_token');
      if (!token) {
        return;
      }

      // Clear existing logs when switching executions
      setLogs([]);

      // First, fetch initial logs from REST endpoint
      try {
        const logResponse = await coreService.get(`/bot/executions/${effectiveExecutionId}/log`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (logResponse.data?.logContent) {
          // Parse logContent string into array of lines
          const logLines = logResponse.data.logContent.split('\n').filter((line: string) => line.trim() !== '');
          setLogs(logLines);
        }
      } catch (error: any) {
        console.error('Error fetching initial execution logs:', error);
        // Continue with WebSocket subscription even if REST fetch fails
      }

      try {
        // Connect to logs WebSocket for real-time updates
        logsWebSocketService.connect();

        // Wait for connection (with timeout)
        await new Promise<void>((resolve, reject) => {
          if (logsWebSocketService.isUserConnected()) {
            resolve();
            return;
          }

          let attempts = 0;
          const maxAttempts = 50; // 5 seconds max wait
          const checkConnection = setInterval(() => {
            attempts++;
            if (logsWebSocketService.isUserConnected()) {
              clearInterval(checkConnection);
              resolve();
            } else if (attempts >= maxAttempts) {
              clearInterval(checkConnection);
              reject(new Error('WebSocket connection timeout'));
            }
          }, 100);
        });

        // Unsubscribe from previous execution
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }

        // Subscribe to execution logs via WebSocket
        unsubscribeRef.current = await logsWebSocketService.subscribeToExecutions(
          [effectiveExecutionId],
          (logData: any) => {
            try {
              // Handle log data from WebSocket
              const logLine = logData.message || logData.log || logData.content || JSON.stringify(logData);
              const timestamp = logData.timestamp || new Date().toISOString();
              const formattedLog = `[${timestamp}] ${logLine}`;
              setLogs(prev => [...prev, formattedLog].slice(compact ? -1000 : -5000)); // Keep last N lines
            } catch (error) {
              console.error('Error processing log data:', error);
              setLogs(prev => [...prev, JSON.stringify(logData)].slice(compact ? -1000 : -5000));
            }
          }
        );
      } catch (error: any) {
        console.error('Error subscribing to logs:', error);
        message.error('Failed to subscribe to log stream');
      }
    };

    subscribeToLogs();

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [effectiveExecutionId, compact, executionIdProp, refreshKey]);

  // Handle create execution button click
  const handleCreateExecution = async () => {
    const token = localStorage.getItem('access_token');
    if (!token || !selectedPortfolio?.portfolioId) {
      message.error('Please select a portfolio first');
      return;
    }

    if (!strategyId) {
      message.error('No strategy selected');
      return;
    }

    try {
      setCreatingExecution(true);

      const createExecutionDto: CreateExecutionDto = {
        strategyId: strategyId,
        portfolioId: selectedPortfolio.id,
        status: executionStatus,
      };

      const response = await coreService.post<ExecutionResponseDto>('bot/executions', createExecutionDto, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.data?.id) {
        const newExecution = response.data;
        // Add new execution to the list
        setExecutions(prev => [newExecution, ...prev]);
        // Select the new execution to stream its logs
        setSelectedExecutionId(newExecution.id);
        message.success('Execution created successfully. Streaming logs...');
      }
    } catch (error: any) {
      console.error('Error creating execution:', error);
      if (error.response?.status === 404) {
        message.error('Execution endpoint not available');
      } else {
        message.error(error.response?.data?.message || error.message || 'Failed to create execution');
      }
    } finally {
      setCreatingExecution(false);
    }
  };

  const handleClear = () => {
    setLogs([]);
  };

  const handleReload = () => {
    fetchLogs();
  };

  const parseLogLevel = (logLine: string): 'info' | 'error' | 'warning' | 'success' => {
    const upperLine = logLine.toUpperCase();
    // The `|LEVEL|` field wins over keywords: "0 failed" in a WARNING/INFO line is not an error.
    const level = upperLine.match(/\|\s*(DEBUG|INFO|WARNING|WARN|ERROR|CRITICAL)\s*\|/)?.[1];
    if (level === 'ERROR' || level === 'CRITICAL') return 'error';
    if (level === 'WARNING' || level === 'WARN') return 'warning';
    if (!level) {
      if (upperLine.includes('WARN')) return 'warning';
      if (upperLine.includes('ERROR') || upperLine.includes('FAILED') || upperLine.includes('EXCEPTION')) {
        return 'error';
      }
    }
    if (upperLine.includes('SUCCESS') || upperLine.includes('COMPLETED') || upperLine.includes('OK')) {
      return 'success';
    }
    return 'info';
  };

  return (
    <LogContainer $compact={compact}>
      <LogHeader $compact={compact}>
        {compact ? (
          <LogTitle as="h4" $compact={compact}>
            Server Logs
          </LogTitle>
        ) : (
          <LogTitle as="h3" $compact={compact}>
            Server Logs {selectedPortfolio ? `- ${selectedPortfolio.portfolioName}` : ''}
          </LogTitle>
        )}
        <LogActions $compact={compact}>
          {showExecutionControls && (
            <ExecutionControls>
              <Select
                value={effectiveExecutionId}
                onChange={(value) => {
                  if (!executionIdProp) {
                    setSelectedExecutionId(value);
                  }
                }}
                disabled={!!executionIdProp}
                style={{ minWidth: 280, maxWidth: 400 }}
                size="small"
                loading={loadingExecutions}
                placeholder="Select execution to view logs"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {executions.map((execution) => (
                  <Select.Option key={execution.id} value={execution.id}>
                    <span style={{ fontWeight: 500 }}>{execution.status}</span>
                    {' - '}
                    <span style={{ color: '#888', fontSize: '12px' }}>
                      // numberFormat-exempt: date
                      {new Date(execution.createdAt).toLocaleString()}
                    </span>
                  </Select.Option>
                ))}
              </Select>
              <Select
                value={executionStatus}
                onChange={(value) => setExecutionStatus(value)}
                style={{ width: 120 }}
                size="small"
                disabled={creatingExecution}
              >
                <Select.Option value="PASSED">PASSED</Select.Option>
                <Select.Option value="FAILED">FAILED</Select.Option>
                <Select.Option value="RUNNING">RUNNING</Select.Option>
                <Select.Option value="PENDING">PENDING</Select.Option>
              </Select>
              <Button
                icon={<PlayCircleOutlined />}
                onClick={handleCreateExecution}
                loading={creatingExecution}
                disabled={!strategyId || !selectedPortfolio?.portfolioId}
                size="small"
                type="primary"
                style={{
                  background: '#0ECB81',
                  borderColor: '#0ECB81',
                  color: '#fff'
                }}
              >
                Create
              </Button>
            </ExecutionControls>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReload}
            loading={loading}
            size="small"
            type={compact ? "text" : "default"}
            style={compact ? {
              color: '#0ECB81',
              padding: '0 8px',
              height: '24px'
            } : {
              color: '#0ECB81',
              borderColor: '#0ECB81',
              background: 'transparent'
            }}
          />
          <Button
            icon={<ClearOutlined />}
            onClick={handleClear}
            size="small"
            danger
            type={compact ? "text" : "default"}
            style={compact ? {
              color: '#F6465D',
              padding: '0 8px',
              height: '24px'
            } : {
              color: '#F6465D',
              borderColor: '#F6465D',
              background: 'transparent'
            }}
          />
        </LogActions>
      </LogHeader>
      <LogContent
        ref={logContentRef}
        $compact={compact}
        onScroll={() => {
          const el = logContentRef.current;
          if (!el) return;
          const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
          setAutoScroll(atBottom);
        }}
      >
        {logs.length === 0 ? (
          <EmptyState $compact={compact}>No logs available</EmptyState>
        ) : (
          logs.map((log, index) => (
            <LogLine key={index} $level={parseLogLevel(log)} $compact={compact}>
              {log}
            </LogLine>
          ))
        )}
      </LogContent>
    </LogContainer>
  );
};

export default LogViewer;

